/**
 * Work out which carousels are in a drop.
 *
 * One rule, applied mechanically: a carousel is the images that share an
 * immediate parent folder, at any depth. Dropping `Q4/Feed A/` and `Q4/Feed B/`
 * makes two carousels; dropping four loose images makes one. There are no
 * special cases for mixed drops, so a stray `cover.jpg` beside two carousel
 * folders becomes a one-frame carousel of its own — flagged, visible, and one
 * click to remove, rather than silently absorbed into a neighbour.
 *
 * Frames run in natural filename order (`2.png` before `10.png`) and can be
 * reordered before upload. OS junk is skipped silently; anything else that is
 * not a JPEG, PNG or WebP is flagged against its carousel, because it usually
 * means the wrong thing was exported.
 *
 * The grouping is pure so it can be tested by calling it; reading pixel sizes
 * needs the browser and is passed in. Mirrors the backend's carousel rules
 * (`api/carousel.py`), which enforce all of this again: a gap here is a worse
 * error message, never a hole.
 */

import type { CopyElement } from '../../api';
import { MAX_CAROUSEL_FRAMES, MIN_CAROUSEL_FRAMES } from '../../constants/assetTypes';
import { CAROUSEL_FRAME_TYPES, MAX_FILE_SIZE, resolveContentType } from './constants';
import { basename, isJunk, normalisePath, type PickedFile } from './olaDetect';

export interface FrameSize {
  width: number;
  height: number;
}

export interface CarouselFrameDraft {
  /** Stable across reorders, for React and for removal. */
  key: string;
  file: File;
  /** Canonical: `image/jpg` is sent as `image/jpeg`, which is what the URL is signed for. */
  contentType: string;
  /** Null when the browser could not decode it. */
  size: FrameSize | null;
  /** Filled in at the details step; required before upload. */
  sourceImageId: string;
  copy: CopyElement[];
}

export interface DetectedCarousel {
  /** The folder the frames share, '' for loose files. Identity for React. */
  folder: string;
  /** Pre-filled from the folder, editable; becomes the asset's name. */
  name: string;
  frames: CarouselFrameDraft[];
  /** Files in the folder that are not frame images, by name. */
  rejected: string[];
  /** Empty means uploadable. Recomputed on every edit. */
  issues: string[];
}

const FRAME_TYPE_NAMES = 'JPEG, PNG or WebP';

function dirname(path: string): string {
  const slash = path.lastIndexOf('/');
  return slash === -1 ? '' : path.slice(0, slash);
}

function stem(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot <= 0 ? filename : filename.slice(0, dot);
}

const byNaturalName = (a: File, b: File) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });

function frameLabel(index: number, frame: CarouselFrameDraft): string {
  return `Frame ${index + 1} (${frame.file.name})`;
}

function sizeLabel({ width, height }: FrameSize): string {
  return `${width}×${height}`;
}

/**
 * Why these frames are not all one pixel size, naming the odd ones, or null.
 *
 * The same reading as the backend's `_size_mismatch`: the reference is the
 * size most frames share, ties to the earlier frame, so the one wrong export
 * is named rather than the five right ones.
 */
function sizeMismatch(frames: CarouselFrameDraft[]): string | null {
  const sized = frames.flatMap((frame, index) => (frame.size ? [{ frame, index }] : []));
  const counts = new Map<string, { size: FrameSize; count: number; first: number }>();
  for (const { frame, index } of sized) {
    const key = sizeLabel(frame.size!);
    const entry = counts.get(key);
    if (entry) entry.count += 1;
    else counts.set(key, { size: frame.size!, count: 1, first: index });
  }
  if (counts.size <= 1) return null;

  const reference = [...counts.values()].sort((a, b) => b.count - a.count || a.first - b.first)[0];
  const odd = sized
    .filter(({ frame }) => sizeLabel(frame.size!) !== sizeLabel(reference.size))
    .map(({ frame, index }) => `${frameLabel(index, frame)} is ${sizeLabel(frame.size!)}`);
  return `Every frame must be the same size. ${odd.join('; ')}; the others are ${sizeLabel(
    reference.size,
  )}.`;
}

/** Everything that blocks this carousel, in the order a user would fix it. */
export function carouselIssues(carousel: Omit<DetectedCarousel, 'issues'>): string[] {
  const issues: string[] = [];
  if (!carousel.name.trim()) issues.push('Give this carousel a name');
  for (const filename of carousel.rejected) {
    issues.push(`${filename} is not a ${FRAME_TYPE_NAMES} image`);
  }
  const count = carousel.frames.length;
  if (count < MIN_CAROUSEL_FRAMES || count > MAX_CAROUSEL_FRAMES) {
    issues.push(
      `A carousel needs ${MIN_CAROUSEL_FRAMES} to ${MAX_CAROUSEL_FRAMES} frames; this one has ${count}`,
    );
  }
  carousel.frames.forEach((frame, index) => {
    if (frame.file.size > MAX_FILE_SIZE) issues.push(`${frameLabel(index, frame)} is too large`);
    if (frame.size === null) issues.push(`${frameLabel(index, frame)}: could not read its size`);
  });
  const mismatch = sizeMismatch(carousel.frames);
  if (mismatch) issues.push(mismatch);
  return issues;
}

/** The carousel with *patch* applied and its issues recomputed. */
export function updateCarousel(
  carousel: DetectedCarousel,
  patch: Partial<Omit<DetectedCarousel, 'issues'>>,
): DetectedCarousel {
  const next = { ...carousel, ...patch };
  return { ...next, issues: carouselIssues(next) };
}

export function moveFrame(carousel: DetectedCarousel, from: number, to: number): DetectedCarousel {
  if (to < 0 || to >= carousel.frames.length || from === to) return carousel;
  const frames = [...carousel.frames];
  const [moved] = frames.splice(from, 1);
  frames.splice(to, 0, moved);
  return updateCarousel(carousel, { frames });
}

export function removeFrame(carousel: DetectedCarousel, key: string): DetectedCarousel {
  return updateCarousel(carousel, { frames: carousel.frames.filter((frame) => frame.key !== key) });
}

export function removeRejected(carousel: DetectedCarousel, filename: string): DetectedCarousel {
  return updateCarousel(carousel, {
    rejected: carousel.rejected.filter((name) => name !== filename),
  });
}

export function isCarouselUploadable(carousel: DetectedCarousel): boolean {
  return carousel.issues.length === 0;
}

/** 1-based numbers of the frames still missing a source image. */
export function framesWithoutSource(carousel: DetectedCarousel): number[] {
  return carousel.frames.flatMap((frame, index) => (frame.sourceImageId ? [] : [index + 1]));
}

/**
 * Group a drop into carousels.
 *
 * *sizes* holds what the browser could read for each frame image; a missing
 * entry reads as "could not decode". *nextKey* hands out frame keys, so a
 * second drop never reuses one.
 */
export function groupCarousels(
  picked: PickedFile[],
  sizes: Map<File, FrameSize | null>,
  nextKey: () => string,
): DetectedCarousel[] {
  const byFolder = new Map<string, { images: File[]; rejected: string[] }>();
  for (const { file, path } of picked) {
    const normalised = normalisePath(path || file.name);
    if (isJunk(normalised)) continue;
    const folder = dirname(normalised);
    const group = byFolder.get(folder) ?? { images: [], rejected: [] };
    if (resolveContentType(file, CAROUSEL_FRAME_TYPES)) group.images.push(file);
    else group.rejected.push(basename(normalised));
    byFolder.set(folder, group);
  }

  return [...byFolder.entries()]
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    .map(([folder, { images, rejected }]) => {
      const frames = [...images].sort(byNaturalName).map((file) => {
        const declared = resolveContentType(file, CAROUSEL_FRAME_TYPES)!;
        return {
          key: nextKey(),
          file,
          contentType: declared === 'image/jpg' ? 'image/jpeg' : declared,
          size: sizes.get(file) ?? null,
          sourceImageId: '',
          copy: [],
        };
      });
      // Loose files have no folder to be named after; the first frame's name
      // is at least distinguishable, unlike a fixed word every drop shares.
      const name = folder ? basename(folder) : stem(frames[0]?.file.name ?? rejected[0] ?? '');
      return updateCarousel({ folder, name, frames, rejected, issues: [] }, {});
    });
}

/** Decode just enough of an image to know its pixel size. Null if the browser cannot. */
export async function readImageSize(file: File): Promise<FrameSize | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size.width > 0 && size.height > 0 ? size : null;
  } catch {
    return null;
  }
}

/** Read every frame image's size, then group. */
export async function detectCarousels(
  picked: PickedFile[],
  nextKey: () => string,
  readSize: (file: File) => Promise<FrameSize | null> = readImageSize,
): Promise<DetectedCarousel[]> {
  const images = picked
    .filter(({ file, path }) => !isJunk(normalisePath(path || file.name)))
    .map(({ file }) => file)
    .filter((file) => resolveContentType(file, CAROUSEL_FRAME_TYPES));
  // One at a time: each read decodes the whole image, and a parent folder of
  // several carousels at full resolution, decoded at once, can run the tab out
  // of memory. Sequential, each bitmap is released before the next is made.
  const sizes = new Map<File, FrameSize | null>();
  for (const file of images) {
    sizes.set(file, await readSize(file));
  }
  return groupCarousels(picked, sizes, nextKey);
}
